/**
 * Wealthica Connection Service
 * 
 * Handles all database operations, connection verification, and connection lifecycle
 * Enhanced with connection management capabilities from client decomposition
 */

import { supabase } from '@/integrations/supabase/client';
import { ensureValidToken } from '@/services/AuthAwareApiService';
import { WealthicaStorage } from '../utils/storage';
import { logger, sanitizeForLogging } from '../utils/logger';
import { getRedirectUri } from '../utils/environment';
import { 
  WealthicaServiceResult, 
  WEALTHICA_ERROR_CODES, 
  createServiceResult, 
  createErrorResult 
} from '../types/errors';
import { 
  WEALTHICA_API_BASE_V2, 
  WEALTHICA_CONNECT_URL_V2, 
  WEALTHICA_CLIENT_ID_V2 
} from '../constants-v2';
import type { WealthicaConnection, WealthicaConnectionOptions, WealthicaConnectionResult } from '../types';
import { isDemoProvider } from '../utils/identifiers';
import { clearCallbackDedupFlags } from '../utils/navigation-flags';

export type ConnectionVerificationResult = WealthicaServiceResult<boolean>;

export interface ConnectionSaveParams {
  userId: string;
  institutionId: string;
  institutionIdentifier?: string;
  providerName: string;
  loginName: string;
  operationId?: string;
  connectionStatus: 'connected' | 'oauth_failed' | 'csrf_failed' | 'error';
  errorCode?: string;
  errorMessage?: string;
}

export class WealthicaConnectionService {

  /**
   * Save connection status to database
   */
  static async saveConnectionStatus(params: ConnectionSaveParams): Promise<void> {
    try {
      logger.info('WealthicaConnectionService', 'Saving connection status', {
        userId: params.userId,
        institutionId: params.institutionId,
        status: params.connectionStatus
      });

      // Use provided institution identifier if available, otherwise fallback to calculation via helper
      const stableIdentifier = params.institutionIdentifier || (
        isDemoProvider(params.providerName, params.institutionIdentifier, params.institutionId)
          ? 'demo'
          : params.providerName
      );

      logger.info('WealthicaConnectionService', 'Institution identifier mapping', {
        institutionId: params.institutionId?.substring(0, 8) + '...',
        providerName: params.providerName,
        providedIdentifier: params.institutionIdentifier,
        calculatedIdentifier: stableIdentifier,
        usedProvidedValue: !!params.institutionIdentifier
      });

      await ensureValidToken('wealthica2-save-connection');

      // Generate operation ID for end-to-end tracing
      const operationId = params.operationId || `conn-service-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // ENHANCED: Log Edge Function invocation attempt
      logger.info('WealthicaConnectionService', 'About to invoke Edge Function', {
        operationId,
        userId: params.userId?.substring(0, 8) + '...',
        institutionId: params.institutionId?.substring(0, 8) + '...',
        stableIdentifier,
        functionName: 'wealthica2-save-connection',
        timestamp: new Date().toISOString()
      });

      // Invoke with small retry to tolerate transient network/CORS blips
      const maxAttempts = 3;
      let lastError: any = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        logger.debug('WealthicaConnectionService', 'Invoking Edge Function (attempt)', {
          attempt,
          maxAttempts,
          operationId
        });
        const { error } = await supabase.functions.invoke('wealthica2-save-connection', {
          body: {
            user_id: params.userId,
            institution_id: params.institutionId,           // Rotating OAuth ID (correct)
            institution_identifier: stableIdentifier,       // Stable identifier (fixed)
            login_name: params.loginName,
            original_provider: params.providerName,
            provider_name: params.providerName,
            operation_id: operationId                       // Pass operation ID for tracing
          }
        });
        if (!error) {
          lastError = null;
          break;
        }
        lastError = error;
        const msg = (error?.message || '').toLowerCase();
        const transient = msg.includes('failed to send a request to the edge function') || msg.includes('failed to fetch') || msg.includes('network');
        if (attempt < maxAttempts && transient) {
          // Simple backoff: 300ms, 600ms
          await new Promise((r) => setTimeout(r, attempt * 300));
          continue;
        }
        // Non-transient or out of attempts
        break;
      }

      // ENHANCED: Log Edge Function response
      logger.info('WealthicaConnectionService', 'Edge Function invocation completed', {
        hasError: !!lastError,
        errorMessage: lastError?.message || 'none',
        timestamp: new Date().toISOString()
      });

      if (lastError) {
        logger.error('WealthicaConnectionService', 'Failed to save connection status', { 
          error: lastError.message || lastError,
          statusCode: lastError.status || 'unknown'
        });
        throw lastError;
      }

      logger.info('WealthicaConnectionService', 'Connection status saved successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('WealthicaConnectionService', 'Failed to save connection status', { error: errorMessage });
      throw error;
    }
  }

  /**
   * Initiate connection to Wealthica
   */
  static async connect(options: WealthicaConnectionOptions = {}): Promise<WealthicaConnectionResult> {
    try {
      logger.info('WealthicaConnectionService', 'Initiating connection to Wealthica');

      // Clear OAuth flow data FIRST, preserving valuable cached data
      logger.info('WealthicaConnectionService', 'Clearing OAuth flow data before new connection');
      WealthicaStorage.clearForLogout();
      // Second line of defense: clear callback de-dup flags to allow fresh processing
      try {
        clearCallbackDedupFlags();
      } catch (e) {
        logger.warn('WealthicaConnectionService', 'Failed to clear callback dedup flags', {
          error: e instanceof Error ? e.message : String(e)
        });
      }

      // Generate a secure state parameter for CSRF protection AFTER clearing
      const state = options.state || crypto.randomUUID();
      
      logger.info('WealthicaConnectionService', '[STATE-LIFECYCLE] Generated OAuth state for connection', {
        stateProvided: !!options.state,
        stateGenerated: !options.state,
        statePreview: state.substring(0, 8) + '...',
        stateLength: state?.length || 0,
        provider: options.provider || 'default',
        timestamp: new Date().toISOString()
      });
      
      // Get redirect URI
      const redirectUri = getRedirectUri();

      // Require userId for token request
      const userId = options.reference;
      if (!userId) {
        return {
          success: false,
          error: 'Missing userId (reference) for Wealthica connect.'
        };
      }

      // Ensure valid token before making API call
      await ensureValidToken('wealthica2-get-token');

      // Call Edge Function to obtain Wealthica session token
      const { data, error } = await supabase.functions.invoke('wealthica2-get-token', {
        body: { loginName: userId },
      });
      
      if (error || !data?.token) {
        logger.error('WealthicaConnectionService', 'Failed to obtain Wealthica session token', { error });
        return {
          success: false,
          error: 'Failed to obtain Wealthica session token'
        };
      }
      
      const token = data.token;
      
      // Server-only tokens: do not persist token in frontend storage
      logger.info('WealthicaConnectionService', 'Obtained session token (not persisted on frontend)', {
        tokenLength: token?.length || 0
      });

      // Construct Wealthica connect URL
      const connectUrl = new URL(WEALTHICA_CONNECT_URL_V2);
      connectUrl.searchParams.append('client_id', WEALTHICA_CLIENT_ID_V2);
      connectUrl.searchParams.append('redirect_uri', redirectUri);
      connectUrl.searchParams.append('state', state);
      connectUrl.searchParams.append('token', token);
      if (options.provider) {
        connectUrl.searchParams.append('provider', options.provider);
      }

      logger.info('WealthicaConnectionService', 'OAuth URL constructed with redirect configuration', {
        redirectUri,
        connectUrl: connectUrl.toString(),
        clientId: WEALTHICA_CLIENT_ID_V2,
        provider: options.provider,
        currentOrigin: window.location.origin,
        state: state,
        registeredRedirectUris: import.meta.env.VITE_WEALTHICA_REDIRECT_URIS || 'not_configured',
        urlBreakdown: {
          baseUrl: WEALTHICA_CONNECT_URL_V2,
          clientId: WEALTHICA_CLIENT_ID_V2,
          redirectUriEncoded: encodeURIComponent(redirectUri),
          stateParam: state,
          tokenPresent: !!token
        }
      });

      logger.info('WealthicaConnectionService', 'Connection URL constructed', sanitizeForLogging({
        redirectUri,
        provider: options.provider || 'wealthica',
        state,
        token: '[REDACTED]'
      }));

      return {
        success: true,
        redirectUrl: connectUrl.toString(),
        state,
      };
    } catch (error) {
      logger.error('WealthicaConnectionService', 'Failed to initiate connection', { error });
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Save OAuth failure status
   */
  static async saveOAuthFailure(
    userId: string,
    partialData: Partial<WealthicaConnection>,
    errorMessage: string
  ): Promise<void> {
    await this.saveConnectionStatus({
      userId,
      institutionId: partialData.institutionId || 'unknown',
      providerName: partialData.providerName || 'wealthica',
      loginName: partialData.loginName || 'unknown',
      connectionStatus: 'oauth_failed',
      errorCode: 'OAUTH_CALLBACK_ERROR',
      errorMessage
    });
  }

  /**
   * Save CSRF failure status
   */
  static async saveCSRFFailure(
    userId: string,
    connectionData: WealthicaConnection
  ): Promise<void> {
    await this.saveConnectionStatus({
      userId,
      institutionId: connectionData.institutionId || 'unknown',
      providerName: connectionData.providerName || 'wealthica',
      loginName: connectionData.loginName || 'unknown',
      connectionStatus: 'csrf_failed',
      errorCode: 'CSRF_STATE_MISMATCH',
      errorMessage: 'Security validation failed: State parameter mismatch'
    });
  }

  /**
   * Save general connection error
   */
  static async saveConnectionError(
    userId: string,
    connectionData: Partial<WealthicaConnection>,
    errorMessage: string
  ): Promise<void> {
    const isTokenError = errorMessage.toLowerCase().includes('token') || 
                        errorMessage.toLowerCase().includes('auth') || 
                        errorMessage.toLowerCase().includes('unauthorized');

    await this.saveConnectionStatus({
      userId,
      institutionId: connectionData.institutionId || 'unknown',
      providerName: connectionData.providerName || 'wealthica',
      loginName: connectionData.loginName || 'unknown',
      connectionStatus: 'error',
      errorCode: isTokenError ? 'TOKEN_ERROR' : 'CONNECTION_ERROR',
      errorMessage: errorMessage.substring(0, 255) // Ensure message fits in database field
    });
  }
}